import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { ChevronLeft, X } from 'lucide-react';
import { useParentMessaging } from '../../parent-messaging/parent-messaging-provider';

const headerIconButtonClass =
  'flex h-[22px] w-[22px] items-center justify-center rounded-full border border-text-muted bg-lobby-card-panel text-text-primary transition-colors hover:brightness-125';
const gameShellScrollContainerSelector = '[data-game-shell-scroll-container="true"]';

export const HeaderBackButton = ({ onClick }: { onClick: () => void }) => (
  <button type="button" aria-label="Go back" onClick={onClick} className={headerIconButtonClass}>
    <ChevronLeft className="h-3 w-3" />
  </button>
);

interface GameHeaderConfig {
  left?: ReactNode;
  center?: ReactNode;
}

interface GameShellContextValue {
  setHeader: Dispatch<SetStateAction<GameHeaderConfig>>;
  setFooter: Dispatch<SetStateAction<ReactNode | null>>;
}

const GameShellContext = createContext<GameShellContextValue | null>(null);

export const useGameHeader = (config: GameHeaderConfig) => {
  const { setHeader } = useContext(GameShellContext) ?? {};
  const left = config.left;
  const center = config.center;
  const ownsLeft = 'left' in config;
  const ownsCenter = 'center' in config;
  useEffect(() => {
    if (!setHeader) return;
    setHeader((prev) => {
      if (ownsLeft && prev.left === left && ownsCenter && prev.center === center) return prev;
      if (ownsLeft && !ownsCenter && prev.left === left) return prev;
      if (ownsCenter && !ownsLeft && prev.center === center) return prev;
      const next: GameHeaderConfig = { ...prev };
      if (ownsLeft) next.left = left;
      if (ownsCenter) next.center = center;
      return next;
    });
    return () => {
      setHeader((prev) => {
        if (!ownsLeft && !ownsCenter) return prev;
        const next: GameHeaderConfig = { ...prev };
        if (ownsLeft) delete next.left;
        if (ownsCenter) delete next.center;
        return next;
      });
    };
  }, [setHeader, left, center, ownsLeft, ownsCenter]);
};

export const useGameFooter = (content: ReactNode | null) => {
  const { setFooter } = useContext(GameShellContext) ?? {};
  useEffect(() => {
    setFooter?.(content);
    return () => setFooter?.(null);
  }, [setFooter, content]);
};

export const useScrollGameContentToTopOnMount = () => {
  useEffect(() => {
    document
      .querySelector<HTMLElement>(gameShellScrollContainerSelector)
      ?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    window.scrollTo(0, 0);
  }, []);
};

export const GameShell = ({ children }: PropsWithChildren) => {
  const parentMessaging = useParentMessaging();
  const [header, setHeader] = useState<GameHeaderConfig>({});
  const [footer, setFooter] = useState<ReactNode | null>(null);
  const contextValue = useMemo(() => ({ setHeader, setFooter }), [setHeader, setFooter]);

  return (
    <GameShellContext value={contextValue}>
      <div className="flex h-dvh flex-col text-text-primary">
        <header className="flex h-14 shrink-0 items-center justify-between px-4">
          <div className="flex min-w-[80px] items-center">{header.left}</div>
          <div className="flex items-center">{header.center}</div>
          <div className="flex min-w-[80px] items-center justify-end">
            <button
              type="button"
              aria-label="Close game"
              onClick={() => parentMessaging?.closeGame()}
              className={headerIconButtonClass}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </header>

        <main
          data-game-shell-scroll-container="true"
          className="flex flex-1 flex-col overflow-y-auto"
        >
          {children}
        </main>

        {footer ? (
          <footer className="sticky bottom-0 z-40 border-t border-border bg-lobby-card-panel px-4 pb-6 pt-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </GameShellContext>
  );
};
